import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { SendHorizonal, Wrench } from "lucide-react";
import { api } from "../lib/api";

const QUICK = ["Battery keeps going dead", "Clicking when I start it", "A light stopped working"];

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [greeting, setGreeting] = useState("Welcome to C.C.C. Auto Electric! Tell me what your vehicle is doing and I'll help you get the details ready for the shop.");
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sources, setSources] = useState([]);
  const sessionRef = useRef(crypto.randomUUID ? crypto.randomUUID() : String(Math.random()));
  const msgsEnd = useRef(null);

  useEffect(() => {
    api("/assistant-public").then((c) => c?.greeting && setGreeting(c.greeting)).catch(() => {});
  }, []);

  useEffect(() => {
    msgsEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, busy]);

  const send = async (text) => {
    if (!text.trim() || busy) return;
    setMessages((m) => [...m, { role: "user", text }]);
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "bot", text: "…", typing: true }]);
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: text }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "", full = "", srcs = [];
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data:")) continue;
          try {
            const payload = JSON.parse(line.slice(5));
            if (payload.delta) full += payload.delta;
            if (payload.sources) srcs = payload.sources;
            if (payload.error) full = payload.error;
          } catch {}
        }
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "bot", text: full };
          return copy;
        });
      }
      setSources(srcs);
      if (srcs.length) setMessages((m) => [...m.slice(0, -1), { role: "bot", text: full }]);
    } catch {
      setMessages((m) => [...m.slice(0, -1), { role: "bot", text: "The assistant is unavailable right now. Please call (562) 425-2027." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {open && (
        <div className="chat-panel" data-testid="chat-panel">
          <div className="chat-head">
            <img src="/assets/ccc-mechanic-mascot-portrait.png" alt="" />
            <div>
              <div className="t">C.C.C. Repair Assistant</div>
              <div className="s"><span className="dot" style={{ width: 6, height: 6, borderRadius: 99, background: "var(--volt)", display: "inline-block" }} /> Approved shop knowledge</div>
            </div>
          </div>
          <div className="chat-msgs" data-testid="chat-messages">
            <div className="msg bot">{greeting}</div>
            {messages.map((m, i) => (
              <div key={i} className={`msg ${m.role === "user" ? "user" : "bot"} ${m.typing ? "typing" : ""}`} data-testid={m.role === "user" ? "chat-msg-user" : "chat-msg-bot"}>
                {m.text}
              </div>
            ))}
            <div ref={msgsEnd} />
          </div>
          {sources.length > 0 && (
            <div className="chat-sources" data-testid="chat-sources">
              Sources: {sources.map((s) => <Link key={s} to={s.startsWith("http") ? s : s}>{s}</Link>).reduce((acc, x) => [...acc, " · ", x], []).slice(1)}
            </div>
          )}
          <div className="chat-quick" data-testid="chat-quick-buttons">
            {QUICK.map((q) => (
              <button key={q} onClick={() => send(q)} disabled={busy}>{q}</button>
            ))}
            <Link to="/repair-request" className="btn btn-blue btn-sm" style={{ padding: "7px 12px", fontSize: 12 }} data-testid="chat-open-form-link">
              <Wrench size={12} /> Full repair form
            </Link>
          </div>
          <form className="chat-input" onSubmit={(e) => { e.preventDefault(); send(input); }}>
            <input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Describe what your vehicle is doing…"
              aria-label="Message the repair assistant" data-testid="chat-input" />
            <button type="submit" aria-label="Send message" data-testid="chat-send-button" disabled={busy}><SendHorizonal size={16} /></button>
          </form>
        </div>
      )}
      <button className="chat-launcher" onClick={() => setOpen((o) => !o)} data-testid="ai-chat-launcher-button">
        <img src="/assets/ccc-mechanic-mascot-portrait.png" alt="" className={open ? "" : "mascot-bob"} />
        {open ? "Hide assistant" : "Ask the mechanic"}
      </button>
    </>
  );
}
